import { Suspense, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    currentSocial: '',
    goals: '',
    budget: '',
    timeline: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4bu73moquaeac4hdv9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          businessName: '',
          businessType: '',
          currentSocial: '',
          goals: '',
          budget: '',
          timeline: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Request a Free Quote
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Tell us about your business and goals, and we'll create a custom social media content marketing package tailored just for you. No obligation, no pressure.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <form id="quote-form" onSubmit={handleSubmit} data-readdy-form className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="businessName" className="block text-gray-700 font-semibold mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                    placeholder="Your Business Name"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="businessType" className="block text-gray-700 font-semibold mb-2">
                  Type of Business *
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">Select your business type</option>
                  <option value="retail">Retail Store</option>
                  <option value="restaurant">Restaurant/Cafe</option>
                  <option value="fitness">Fitness/Wellness</option>
                  <option value="beauty">Beauty/Salon</option>
                  <option value="professional">Professional Services</option>
                  <option value="realestate">Real Estate</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="currentSocial" className="block text-gray-700 font-semibold mb-2">
                  Current Social Media Presence
                </label>
                <input
                  type="text"
                  id="currentSocial"
                  name="currentSocial"
                  value={formData.currentSocial}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                  placeholder="Instagram, Facebook, etc. (or 'None' if starting fresh)"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="goals" className="block text-gray-700 font-semibold mb-2">
                  What are your main goals? *
                </label>
                <textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm resize-none"
                  placeholder="e.g., Increase brand awareness, generate leads, drive foot traffic..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.goals.length}/500 characters
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="budget" className="block text-gray-700 font-semibold mb-2">
                    Monthly Budget Range *
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select budget range</option>
                    <option value="under500">Under $500</option>
                    <option value="500-1000">$500 - $1,000</option>
                    <option value="1000-2000">$1,000 - $2,000</option>
                    <option value="2000plus">$2,000+</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="timeline" className="block text-gray-700 font-semibold mb-2">
                    When do you want to start? *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 pr-8 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediately">Immediately</option>
                    <option value="1-2weeks">1-2 weeks</option>
                    <option value="1month">Within a month</option>
                    <option value="exploring">Just exploring options</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="additionalInfo" className="block text-gray-700 font-semibold mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm resize-none"
                  placeholder="Any other details you'd like us to know..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.additionalInfo.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We've received your quote request and will get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-xl">
                  <i className="ri-error-warning-line mr-2"></i>
                  Oops! Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Request Your Free Quote'}
              </button>

              <p className="text-center text-sm text-gray-600 mt-4">
                We'll review your information and send you a custom quote within 24 hours. No spam, we promise!
              </p>
            </form>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              What Happens Next?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-mail-send-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">We Review Your Request</h3>
                <p className="text-gray-600">Our team carefully reviews your business needs and goals</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full mx-auto mb-4">
                  <i className="ri-file-list-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Custom Quote Created</h3>
                <p className="text-gray-600">We create a tailored package and pricing just for you</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-phone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">We Reach Out</h3>
                <p className="text-gray-600">We'll contact you within 24 hours to discuss your quote</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default QuotePage;